import { z } from 'zod';
// Node Type — VARCHAR(40) in DB. Single source for the api write guard (atlas.service KNOWN_TYPES)
// and the admin editor's type picker (DM #2262).
export const KNOWN_ATLAS_NODE_TYPES = [
    'vision',
    'mission',
    'layer',
    'module',
    'component',
    'table',
    'tool',
    'rule',
    'term',
    'integration',
    'misc',
    'decision',
    'ai_profile',
    'user',
    // Mirror nodes (Entity OS, saved query/composition); the prefixes keep them collision-safe.
    'entity:project',
    'entity:organization',
    'entity:brand',
    'entity:product',
    'entity:unit',
    'entity:designSystem',
    'query:saved',
    'composition:saved',
];
// Schema accepts any non-empty string (plugin extensibility); service validates against KNOWN_TYPES
export const atlasNodeTypeEnum = z.string().min(1).max(40);
// Node Source enum
export const atlasNodeSourceEnum = z.enum(['manual', 'introspected', 'ai', 'hybrid']);
// Drift Status enum
export const atlasDriftStatusEnum = z.enum(['ok', 'stale', 'conflict']);
// Edge Kind enum — includes composed_of + enables (gh#604)
export const atlasEdgeKindEnum = z.enum([
    'depends_on',
    'reads',
    'writes',
    'implements',
    'relates',
    'owns',
    'references',
    'composed_of',
    'enables',
]);
// Node CREATE schema
export const atlasNodeCreateSchema = z.object({
    slug: z.string().max(100).optional(), // Auto-generated if not provided
    type: atlasNodeTypeEnum,
    title: z.string().min(1, 'Title is required').max(255),
    contentMd: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
    parentId: z.coerce.number().int().positive().optional().nullable(),
    position: z.object({ x: z.number(), y: z.number() }).optional().nullable(),
    importance: z.coerce.number().int().min(1).max(5).optional().default(3),
    ownerProfileId: z.coerce.number().int().positive().optional().nullable(),
    aiGenerated: z.boolean().optional().default(false),
    source: atlasNodeSourceEnum.optional().default('manual'),
});
// Node UPDATE schema
export const atlasNodeUpdateSchema = z.object({
    slug: z.string().max(100).optional(),
    type: atlasNodeTypeEnum.optional(),
    title: z.string().min(1).max(255).optional(),
    contentMd: z.string().optional().nullable(),
    metadata: z.record(z.unknown()).optional(),
    parentId: z.coerce.number().int().positive().optional().nullable(),
    position: z.object({ x: z.number(), y: z.number() }).optional().nullable(),
    importance: z.coerce.number().int().min(1).max(5).optional(),
    ownerProfileId: z.coerce.number().int().positive().optional().nullable(),
    driftStatus: atlasDriftStatusEnum.optional(),
});
// Node LIST QUERY schema
export const atlasNodeListQuerySchema = z.object({
    page: z.coerce.number().int().positive().optional().default(1),
    perPage: z.coerce.number().int().min(1).max(2000).optional().default(20),
    search: z.string().optional(), // Search in title/contentMd
    type: atlasNodeTypeEnum.optional(),
    parentId: z.coerce.number().int().positive().optional().nullable(),
    source: atlasNodeSourceEnum.optional(),
    driftStatus: atlasDriftStatusEnum.optional(),
    excludeDeleted: z.enum(['true', 'false']).optional().default('true').transform((v) => v === 'true'),
    sortBy: z.enum(['created_at', 'updated_at', 'title', 'importance']).optional().default('updated_at'),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});
// Edge CREATE schema
export const atlasEdgeCreateSchema = z.object({
    sourceId: z.coerce.number().int().positive(),
    targetId: z.coerce.number().int().positive(),
    kind: atlasEdgeKindEnum,
    weight: z.coerce.number().int().optional().default(1),
    metadata: z.record(z.unknown()).optional(),
});
// Context Pack schema
export const atlasContextPackSchema = z.object({
    scope: z.union([
        z.array(z.string()), // Array of types or slugs
        z.string(), // Single type or slug
    ]).optional(),
    depth: z.coerce.number().int().min(1).max(5).optional().default(2),
    tokenBudget: z.coerce.number().int().positive().optional().default(6000),
    nodeIds: z.array(z.coerce.number().int().positive()).optional(), // Explicit node IDs
});
//# sourceMappingURL=atlas.schema.js.map